import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/core/auth/AuthContext';
import { UserNewService } from '../services/userNew.service';
import type { UserNewFormData } from '../types/userNew.types';

const initialFormData: UserNewFormData = {
  // === Thông tin cá nhân ===
  full_name: '',
  gender: 'male',
  birthday: '',
  birth_year: undefined,
  id_number: '',
  id_issue_date: '',
  id_issue_place: '',
  nationality: 'Việt Nam',
  marital_status: 'single',
  phone: '',
  email: '',
  permanent_address: '',
  current_address: '',
  avatar_path: undefined,
  _avatarFile: undefined,

  // === Thông tin tài khoản ===
  username: '',
  temporary_password: '',
  account_email: '',
  require_password_change: true,

  // === Thông tin công việc ===
  job_title: '',
  role_code: 'pm',
  direct_manager_id: '',
  join_date: new Date().toISOString().split('T')[0],
  employment_type: 'full_time',
  status: 'active',
  recruitment_source: '',
  notes: '',

  // === Học vấn & Kỹ năng ===
  education_level: '',
  university: '',
  major: '',
  graduation_year: undefined,
  skills: [],

  // === Liên hệ khẩn cấp ===
  emergency_contact_name: '',
  emergency_contact_phone: '',
  emergency_contact_relationship: '',
};

export function useUserNew() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<UserNewFormData>(initialFormData);
  const [isSaving, setIsSaving] = useState(false);

  const updateField = (field: keyof UserNewFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const generateRandomPassword = () => {
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$!';
    const pwd = Array.from({ length: 12 }, () =>
      charset[Math.floor(Math.random() * charset.length)]
    ).join('');
    updateField('temporary_password', pwd);
  };

  const handleNext = () => {
    if (currentStep < 3) setCurrentStep(prev => prev + 1);
  };

  const handlePrev = () => {
    if (currentStep > 1) setCurrentStep(prev => prev - 1);
  };

  const handleSave = async () => {
    // Guard: bắt buộc phải có company_id
    if (!companyId) {
      toast.error('Không xác định được công ty. Vui lòng đăng nhập lại.');
      return;
    }

    // Guard: validate trường bắt buộc
    if (!formData.full_name?.trim()) {
      toast.error('Vui lòng nhập họ và tên nhân sự.');
      return;
    }

    setIsSaving(true);
    try {
      const profile = await UserNewService.createUser(formData, companyId);
      toast.success(`Đã tạo nhân sự ${profile.full_name} (${profile.user_code}) thành công`);
      navigate('/users');
    } catch (error: any) {
      toast.error('Lỗi khi thêm nhân sự: ' + (error?.message || 'Lỗi không xác định'));
    } finally {
      setIsSaving(false);
    }
  };

  return {
    currentStep,
    formData,
    isSaving,
    companyId,
    updateField,
    generateRandomPassword,
    handleNext,
    handlePrev,
    handleSave,
    setCurrentStep,
  };
}
